import {
  filtersUpdated,
  showColumnsUpdated,
  sortUpdated,
  tagsUpdated,
} from './table.slice';
import { showToast } from '../toast/toast.slice';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PER_PAGE,
} from '../../misc/misc.constants';
import { URL_PARAMETERS } from '../../misc/url.constants';
import { COLUMNS } from '../../misc/columns.constants';
import { gtmEvents } from '../../misc/gtm.constants';
import { triggerGtmEvent } from '../../misc/functions';
import { fetchWebsitesData } from './table.api';
import type { AppThunk } from '../../store/store';
import type { Filters, SortState } from '../../types';

export function checkForUpdates(): AppThunk<Promise<void>> {
  return async function (dispatch, getState) {
    const currentETag = getState().table.websitesDataETag;
    try {
      const response = await fetchWebsitesData({ method: 'HEAD' });
      const newETag = response.headers.get('ETag');
      if (currentETag && newETag && currentETag !== newETag) {
        dispatch(
          showToast(
            'New data is available. The page will be reloaded in 5 seconds.',
          ),
        );
        triggerGtmEvent(gtmEvents.freshDataAvailable);
        setTimeout(() => {
          window.location.reload();
        }, 5_000);
      }
    } catch (e) {
      console.error(e);
    }
  };
}

export function updateURL(): AppThunk {
  return function (_dispatch, getState) {
    const filters = getState().table.filters;
    const tags = getState().table.tags;
    const currentPage = getState().table.currentPage;
    const perPage = getState().table.perPage;
    const sort = getState().table.sort;
    const showColumns = getState().table.showColumns;
    const keysToUpdate = [
      ...Object.keys(filters),
      COLUMNS.tags,
      ...Object.values(URL_PARAMETERS),
    ];
    const params = new URLSearchParams(window.location.search);
    // remove duplicated url keys in wrong case
    for (const [urlKey] of params) {
      for (const key of keysToUpdate) {
        if (urlKey.toLowerCase() === key.toLowerCase() && urlKey !== key) {
          console.log('delete urlKey', urlKey);
          params.delete(urlKey);
          break;
        }
      }
    }

    for (const key of keysToUpdate) {
      // filters
      if (Object.keys(filters).includes(key)) {
        if (filters[key]) {
          params.set(key, filters[key]);
        } else {
          params.delete(key);
        }

        continue;
      }

      // tags
      if (key === COLUMNS.tags) {
        if (tags.length === 0) {
          params.delete(key);
        } else {
          params.set(key, tags.join());
        }
        continue;
      }

      // misc
      if (Object.values(URL_PARAMETERS).includes(key)) {
        switch (key) {
          case URL_PARAMETERS.showColumns:
            if (showColumns.length === 0) {
              params.delete(key);
            } else {
              params.set(key, showColumns.join());
            }
            break;

          // pagination
          case URL_PARAMETERS.currentPage:
            if (Number(currentPage) !== DEFAULT_CURRENT_PAGE) {
              params.set(key, String(currentPage));
            } else {
              params.delete(key);
            }
            break;

          case URL_PARAMETERS.perPage:
            if (Number(perPage) !== DEFAULT_PER_PAGE) {
              params.set(key, String(perPage));
            } else {
              params.delete(key);
            }
            break;

          // sort
          case URL_PARAMETERS.column:
          case URL_PARAMETERS.direction: {
            const value = sort[key as keyof SortState];
            if (value) {
              params.set(key, value);
            } else {
              params.delete(key);
            }
            break;
          }
        }

        continue;
      }
    }

    // check if showColumns equal to defaultShowColumns
    // in this case we don't want to save it in URL
    const defaultShowColumns = getState().table.defaultShowColumns;
    if (
      showColumns.length === defaultShowColumns.length &&
      showColumns.every((column) => defaultShowColumns.includes(column))
    ) {
      params.delete(URL_PARAMETERS.showColumns);
    }

    // check if showColumns equal to renderableColumns
    // in this case we should use alias 'all'
    const renderableColumns = getState().table.renderableColumns;
    if (
      showColumns.length === renderableColumns.length &&
      showColumns.every((column) => renderableColumns.includes(column))
    ) {
      params.set(URL_PARAMETERS.showColumns, 'all');
    }

    // check if showColumns is an empty array
    // in this case we should use alias 'none'
    if (
      params.get(URL_PARAMETERS.showColumns) === null &&
      showColumns.length === 0
    ) {
      params.set(URL_PARAMETERS.showColumns, 'none');
    }

    const search = params.toString();
    const newUrl = search
      ? `${window.location.pathname}?${search}`
      : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
  };
}

export function resetFilters(): AppThunk {
  return function (dispatch, getState) {
    const filters: Filters = {};
    for (const filter in getState().table.filters) {
      filters[filter] = '';
    }
    dispatch(filtersUpdated(filters));
  };
}

export function resetTags(): AppThunk {
  return function (dispatch) {
    dispatch(tagsUpdated([]));
  };
}

export function resetSort(): AppThunk {
  return function (dispatch) {
    dispatch(
      sortUpdated({
        column: '',
        direction: '',
      }),
    );
  };
}

export function updateShowColumns(showColumns: string[]): AppThunk {
  return function (dispatch) {
    dispatch(showColumnsUpdated(showColumns));
  };
}
